import { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { EnhancedSearch } from "../components/EnhancedSearch";
import { CopyButton } from "../components/CopyButton";
import { SEO } from "../components/SEO";
import { routeSeo } from "../content/seoRoutes";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useDataLoader } from "../hooks/useDataLoader";
import { loadSTDCodes } from "../utils/dataLoader";
import type { STDCode } from "../types/data";
import { Phone, MapPin, Building2 } from "lucide-react";

export function StdCodesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedState = searchParams.get('state') || "";
  const urlSearchTerm = searchParams.get('q') || "";

  const [searchTerm, setSearchTerm] = useState(urlSearchTerm);
  const { data: stdCodes, loading, error } = useDataLoader(loadSTDCodes);

  // Debounced search term for grid filtering to avoid lagging while typing
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearchTerm);
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        const trimmed = searchTerm.trim();
        if (trimmed) next.set('q', trimmed);
        else next.delete('q');
        return next;
      }, { replace: true });
    }, 200);
    return () => clearTimeout(id);
  }, [searchTerm, setSearchParams]);

  // Sync URL search parameters to state on mount/back navigation
  useEffect(() => {
    setSearchTerm(urlSearchTerm);
    setDebouncedSearch(urlSearchTerm);
  }, [urlSearchTerm]);

  const handleFilterChange = useCallback((filterValue: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (filterValue) next.set('state', filterValue);
      else next.delete('state');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // State changes triggered manually by button clicks
  const handleStateChange = useCallback((state: string) => {
    handleFilterChange(state);
  }, [handleFilterChange]);

  // Get unique states for filtering
  const states = useMemo(() => {
    const source = stdCodes ?? [];
    const uniqueStates = Array.from(new Set(source.map(code => code.state)));
    return uniqueStates.sort();
  }, [stdCodes]);

  // Get search suggestions
  const suggestions = useMemo(() => {
    if (searchTerm.length < 2) return [];
    
    const source = stdCodes ?? [];
    const cities = Array.from(new Set(source.map(code => code.city)));
    const codes = source.map(code => code.code);
    const allSuggestions = [...cities, ...states, ...codes];
    
    return allSuggestions.filter(item => 
      item.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 8);
  }, [searchTerm, states, stdCodes]);

  // Filter data
  const filteredStdCodes = useMemo(() => {
    let filtered: STDCode[] = (stdCodes ?? []);
    
    // Filter by search term
    if (debouncedSearch) {
      const term = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (code) =>
          code.code.includes(term) ||
          code.city.toLowerCase().includes(term) ||
          code.state.toLowerCase().includes(term)
      );
    }
    
    // Filter by state
    if (selectedState) {
      filtered = filtered.filter(code => code.state === selectedState);
    }
    
    return filtered;
  }, [debouncedSearch, selectedState, stdCodes]);

  // State filters
  const stateFilters = useMemo(() => [
    { label: "All States", value: "", active: selectedState === "" },
    ...states.map(state => ({
      label: state,
      value: state,
      active: selectedState === state
    }))
  ], [states, selectedState]);



  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": "Indian STD Codes Database",
    "description": "Complete database of Indian STD (Standard Trunk Dialing) codes for telephone area codes",
    "keywords": ["STD codes", "telephone area codes", "landline codes", "Indian phone codes"],
    "license": "https://creativecommons.org/licenses/by/4.0/",
    "creator": {
      "@type": "Organization",
      "name": "Indian Codes Hub"
    },
    "distribution": {
      "@type": "DataDownload",
      "contentUrl": `${window.location.origin}/std-codes`,
      "encodingFormat": "text/html"
    }
  };

  return (
    <>
      {loading && (
        <div className="text-center py-4 text-gray-500">Loading STD codes...</div>
      )}
      {error && (
        <div className="text-center py-4 text-red-600">Failed to load STD codes. Please try again later.</div>
      )}
      <SEO
        {...routeSeo('std-codes')}
        keywords="STD codes, telephone area codes, landline codes, phone area codes, Indian telephone codes"
        structuredData={structuredData}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
            Indian STD Codes Finder
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Find Indian STD (Standard Trunk Dialing) codes for landline numbers. 
            Search by city or state with instant copy functionality for all telephone area codes.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mt-4 sm:mt-6 text-xs sm:text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <Phone className="h-4 w-4" />
              <span>{stdCodes?.length ?? 0} STD Codes</span>
            </div>
            <div className="flex items-center space-x-2">
              <Building2 className="h-4 w-4" />
              <span>{Array.from(new Set((stdCodes ?? []).map(c => c.city))).length} Cities</span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="h-4 w-4" />
              <span>{states.length} States</span>
            </div>
          </div>
        </div>

        {/* Enhanced Search */}
        <EnhancedSearch
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search STD codes, cities, or states..."
          suggestions={suggestions}
          filters={stateFilters.slice(0, 6)}
          onFilterChange={handleFilterChange}
          resultsCount={filteredStdCodes.length}
        />

        {/* Quick State Filter Buttons */}
        {!searchTerm && (
          <div className="flex flex-wrap gap-2 mb-6 justify-center px-4">
            <Button
              variant={selectedState === "" ? "default" : "outline"}
              size="sm"
              onClick={() => handleStateChange("")}
              className="text-xs sm:text-sm"
            >
              All States
            </Button>
            {[
              'Maharashtra', 'Delhi', 'Karnataka', 
              'Tamil Nadu', 'Uttar Pradesh', 'Gujarat',
              'West Bengal', 'Rajasthan', 'Telangana',
              'Andhra Pradesh', 'Kerala', 'Punjab'
            ].filter(state => states.includes(state)).map((state) => (
              <Button
                key={state}
                variant={selectedState === state ? "default" : "outline"}
                size="sm"
                onClick={() => handleStateChange(state)}
                className="text-xs sm:text-sm"
              >
                {state}
              </Button>
            ))}
          </div>
        )}

        {/* Results Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredStdCodes.map((code) => (
            <Card key={`${code.code}-${code.city}`} className="hover:shadow-lg transition-all duration-300 group">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-purple-600 group-hover:text-purple-700">
                    {code.code}
                  </span>
                  <div className="flex items-center space-x-2">
                    <Badge variant="secondary" className="text-xs">
                      {code.state}
                    </Badge>
                    <CopyButton text={code.code} size="sm" />
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">City</p>
                    <div className="flex items-center justify-between">
                      <p className="font-medium">{code.city}</p>
                      <CopyButton text={code.city} variant="ghost" size="sm" />
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-100">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        const fullCode = `${code.code} - ${code.city}, ${code.state}`;
                        navigator.clipboard.writeText(fullCode);
                      }}
                    >
                      Copy Full Info
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* No Results */}
        {filteredStdCodes.length === 0 && (
          <div className="text-center py-16">
            <Phone className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No STD codes found</h3>
            <p className="text-gray-500 mb-4">
              Try adjusting your search terms or filters to find what you're looking for.
            </p>
            <Button variant="outline" onClick={() => { setSearchTerm(""); handleFilterChange(""); }}>
              Clear All Filters
            </Button>
          </div>
        )}

        {/* SEO Content Section */}
        <div className="mt-16 bg-gray-50 rounded-lg p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">About STD Codes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-600">
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">What are STD Codes?</h3>
              <p className="text-sm leading-relaxed">
                STD (Standard Trunk Dialing) codes are telephone area codes used in India to identify 
                specific geographic regions for landline phone numbers. They range from 2-5 digits.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">How to Use STD Codes</h3>
              <p className="text-sm leading-relaxed">
                When making a long-distance call within India, dial 0 followed by the STD code, 
                then the local phone number. For example: 0-11-xxxxxxxx for Delhi numbers.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}